import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiEyeOff, FiGrid } from 'react-icons/fi';
import { ROUTES } from '../../app/routes';
import { listInstalledWidgets, updateInstallationConfig, widgetHasSurface, type InstalledWidget } from '../../services/myToolsApi';
import WidgetFrame from './WidgetFrame';
import { withPaused } from './widgetInstallControls';
import { useWidgetInstallsChanged } from './widgetEvents';
import { currentUserKey } from './widgetStorage';
import {
  arrangeTiles, cycleTileSize, loadHomeLayout, moveTile, placeTileBefore, pruneLayout, saveHomeLayout,
  setTileHidden, tileSize, TILE_SIZE_LABEL, type HomeLayout,
} from './widgetLayout';
import '../../styles/Widgets.css';

const MAX_TILES = 12;
const TILE_HEIGHT = 160;
const TALL_TILE_HEIGHT = 340;

/** "My Widgets" section of the home/welcome screen — small fixed-height frames for
 *  installed widgets whose manifest opts into the `tile` surface. The person can reorder,
 *  resize and hide tiles; the layout is remembered per user on this device. */
const WidgetTiles: React.FC = () => {
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState<InstalledWidget[] | null>(null);
  const [failed, setFailed] = useState(false);
  const userKey = useMemo(() => currentUserKey(), []);
  const [layout, setLayout] = useState<HomeLayout>(() => loadHomeLayout(userKey));
  const [editing, setEditing] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  useWidgetInstallsChanged(() => setReloadKey(k => k + 1));

  useEffect(() => {
    const controller = new AbortController();
    listInstalledWidgets(controller.signal)
      .then(list => {
        if (controller.signal.aborted) return;
        // Paused tiles stay in the layout (unlike before) so the person can see they're
        // paused and resume in place, instead of the tile just vanishing with no trace.
        const tiles = list.filter(w => widgetHasSurface(w.manifest, 'tile'));
        setWidgets(tiles);
        // Forget layout entries for widgets that were uninstalled since last visit.
        setLayout(prev => {
          const pruned = pruneLayout(prev, tiles.map(w => w.id));
          if (JSON.stringify(pruned) !== JSON.stringify(prev)) saveHomeLayout(userKey, pruned);
          return pruned;
        });
      })
      .catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [userKey, reloadKey]);

  const update = useCallback((next: HomeLayout) => {
    setLayout(next);
    saveHomeLayout(userKey, next);
  }, [userKey]);

  const resume = useCallback(async (widget: InstalledWidget) => {
    setResumingId(widget.id);
    try {
      const controls = { ...widget.controls, paused: false };
      const merged = withPaused(widget.rawConfigValues, false);
      await updateInstallationConfig(widget.id, widget.installationId, merged);
      setWidgets(prev => (prev ?? []).map(w => w.id === widget.id ? { ...w, paused: false, controls, rawConfigValues: merged } : w));
    } catch {
      // Best-effort from a tile; Installed → Configuration always has Resume too.
    } finally {
      setResumingId(null);
    }
  }, []);

  const { visible, hidden } = useMemo(() => arrangeTiles(widgets ?? [], layout), [widgets, layout]);
  const shown = visible.slice(0, MAX_TILES);
  const visibleIds = useMemo(() => visible.map(w => w.id), [visible]);
  // Tiles stay in a fixed DOM order and are placed with CSS `order`. Reordering a keyed list
  // makes React move the tile's DOM node, and moving an iframe reloads it — a second load the
  // frame treats as the widget navigating away, so a healthy widget was killed on every reorder.
  const tilePosition = new Map(shown.map((w, i) => [w.id, i]));
  const domOrder = [...shown].sort((a, b) => a.id.localeCompare(b.id));

  if (failed || widgets === null) return null;

  return (
    <motion.div className="wv-section"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25, duration: 0.35 }}>
      <div className="wv-section__header wg-tiles__header">
        <span className="wv-section__header-icon"><FiGrid size={12} /></span>
        <span>My Widgets</span>
        {widgets.length > 0 && (
          <button
            type="button"
            className={`wg-tiles__edit${editing ? ' wg-tiles__edit--active' : ''}`}
            onClick={() => { setEditing(e => !e); setDragId(null); }}
            title={editing ? 'Finish editing the layout' : 'Reorder, resize or hide tiles'}
          >
            {editing ? 'Done' : 'Edit layout'}
          </button>
        )}
      </div>

      {widgets.length === 0 ? (
        <button type="button" className="wv-alerts-empty wg-tiles-empty" onClick={() => navigate(`${ROUTES.MY_TOOLS}?view=marketplace`)}>
          <FiGrid size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
          <span>Add widgets from the Marketplace</span>
        </button>
      ) : (
        <>
          {shown.length === 0 && (
            <div className="wg-tiles__hidden">All your widgets are hidden from the home screen — use “Edit layout” to show them again.</div>
          )}
          <div className="wg-tiles">
            {domOrder.map(w => {
              const index = tilePosition.get(w.id) ?? 0;
              const size = tileSize(layout, w.id);
              const frameHeight = size === 'tall' || size === 'lg' ? TALL_TILE_HEIGHT : TILE_HEIGHT;
              return (
                <div
                  key={w.id}
                  style={{ order: index }}
                  className={`wg-tile wg-tile--${size}${editing ? ' wg-tile--editing' : ''}${dragId === w.id ? ' wg-tile--dragging' : ''}`}
                  draggable={editing}
                  onDragStart={editing ? e => { setDragId(w.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
                  onDragEnd={editing ? () => setDragId(null) : undefined}
                  onDragOver={editing ? e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } : undefined}
                  onDrop={editing ? e => { e.preventDefault(); if (dragId) update(placeTileBefore(layout, visibleIds, dragId, w.id)); setDragId(null); } : undefined}
                >
                  <div className="wg-tile__head">
                    <span className="wg-tile__icon">{w.icon}</span>
                    <span className="wg-tile__name">{w.name}</span>
                    {w.paused && <span className="wg-chip wg-chip--paused">Paused</span>}
                    {editing ? (
                      <div className="wg-tile__controls" role="group" aria-label={`Layout controls for ${w.name}`}>
                        <button type="button" className="wg-tile__ctl" title="Move earlier" disabled={index === 0} onClick={() => update(moveTile(layout, visibleIds, w.id, -1))}><FiChevronLeft size={12} /></button>
                        <button type="button" className="wg-tile__ctl" title="Move later" disabled={index === shown.length - 1} onClick={() => update(moveTile(layout, visibleIds, w.id, 1))}><FiChevronRight size={12} /></button>
                        <button type="button" className="wg-tile__ctl wg-tile__ctl--label" title="Change size" onClick={() => update(cycleTileSize(layout, w.id))}><span aria-hidden="true">⤢</span> {TILE_SIZE_LABEL[size]}</button>
                        <button type="button" className="wg-tile__ctl" title="Hide from home screen" onClick={() => update(setTileHidden(layout, w.id, true))}><FiEyeOff size={12} /></button>
                      </div>
                    ) : (
                      <button type="button" className="wg-tile__open" title="Open full size" onClick={() => navigate(`${ROUTES.WIDGETS}/${w.id}`)}>
                        <FiArrowRight size={13} />
                      </button>
                    )}
                  </div>
                  <div className="wg-tile__body">
                    {w.paused ? (
                      <div className="wg-frame" style={{ height: frameHeight }}>
                        <div className="wg-frame__state wg-frame__state--paused">
                          <span>⏸ Paused</span>
                          {!editing && (
                            <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" disabled={resumingId === w.id} onClick={() => resume(w)}>
                              {resumingId === w.id ? 'Resuming…' : 'Resume'}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <WidgetFrame
                        toolId={w.id}
                        installationId={w.installationId}
                        manifest={w.manifest}
                        grantedSandbox={w.grantedSandbox}
                        config={w.configValues}
                        surface="tile"
                        height={frameHeight}
                        autoResize={false}
                      />
                    )}
                    {editing && <div className="wg-tile__shield" aria-hidden="true" />}
                  </div>
                </div>
              );
            })}
          </div>
          {editing && hidden.length > 0 && (
            <div className="wg-tiles__hidden">
              <span>Hidden from home:</span>
              {hidden.map(w => (
                <button key={w.id} type="button" className="wg-chip wg-tiles__show" title="Show on home screen" onClick={() => update(setTileHidden(layout, w.id, false))}>
                  {w.icon} {w.name} · Show
                </button>
              ))}
            </div>
          )}
          {editing && (
            <p className="wg-tiles__hint">Drag tiles to reorder, or use the arrows. Sizes cycle Small → Wide → Tall → Large. The layout is saved for you on this device.</p>
          )}
          {!editing && visible.length > MAX_TILES && (
            <p className="wg-tiles__hint">Showing {MAX_TILES} of {visible.length} tiles — hide some in “Edit layout” or open the <button type="button" className="wg-tiles__link" onClick={() => navigate(ROUTES.WIDGETS)}>Widgets page</button>.</p>
          )}
        </>
      )}
    </motion.div>
  );
};

export default WidgetTiles;
